// Session RC1: "Overtime past the block agreed", on a technician visit's details in the Schedule (office and admin; the
// page is the office app's). The technician block pays a flat sum for up to its hours on site; time past it is paid at the
// technician rate only when the office records here that it was agreed on the day. Shows who recorded it and when.
//
// PREVIEW FIRST. Ticking or clearing asks the route's own preview, which says what the change does and names any already
// approved week it would reprice; the office then confirms (and, when a week is named, ticks that it may move). A 409
// approved_weeks_affected at the commit is the server's sentence, relayed, and asks again. Nothing is sent to anyone, and
// whether the customer pays for the extra time is the office's own agreement with them, never written here.
import { useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { RateCardApprovedWeek } from "../../lib/api";
import { Button } from "../../components/Button";
import { formatDateLong, formatDateTime } from "../../lib/format";

export function TechOvertimeControl({
  jobId,
  agreedAt,
  agreedByName,
  onChanged,
}: {
  jobId: string;
  agreedAt: string | null | undefined;
  agreedByName: string | null | undefined;
  onChanged: (next: { agreedAt: string | null; agreedByName: string | null }) => void;
}) {
  const agreed = agreedAt != null;
  const [pending, setPending] = useState<{ agreed: boolean; message: string; weeks: RateCardApprovedWeek[] } | null>(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function preview(want: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.schedule.techOvertime(jobId, { agreed: want, preview: true });
      if ("preview" in res) {
        setPending({ agreed: want, message: res.message, weeks: res.approvedWeeksMoved });
        setAck(false);
      }
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "Could not check that change. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await api.schedule.techOvertime(jobId, { agreed: pending.agreed, ...(pending.weeks.length > 0 && ack ? { affectsApprovedWeeks: true } : {}) });
      onChanged(pending.agreed ? { agreedAt: new Date().toISOString(), agreedByName: null } : { agreedAt: null, agreedByName: null });
      setPending(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body?.error === "approved_weeks_affected") {
        setError(e.message);
        void preview(pending.agreed);
      } else setError(e instanceof ApiError && e.message ? e.message : "Could not save that change. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="py-1 text-sm" data-testid="tech-overtime">
      <label className="flex items-center gap-2 text-ele-strong max-sm:min-h-11">
        <input type="checkbox" checked={pending ? pending.agreed : agreed} disabled={busy} onChange={(e) => void preview(e.target.checked)} />
        Overtime past the block agreed
      </label>
      {agreed && !pending && (
        <p className="pl-6 text-xs text-ele-body">
          Recorded{agreedByName ? ` by ${agreedByName}` : ""}, {formatDateTime(agreedAt)}.
        </p>
      )}
      {pending && (
        <div role="status" className="mt-1 rounded-ele border border-ele-purple-accent px-3 py-2 text-xs text-ele-body">
          <p className="text-ele-strong">{pending.message}</p>
          {pending.weeks.length > 0 && (
            <>
              <p className="mt-1">
                This moves {pending.weeks.length} already approved week{pending.weeks.length === 1 ? "" : "s"}:{" "}
                {pending.weeks.map((w) => `${w.engineerName}, week beginning ${formatDateLong(w.weekStarting)}`).join("; ")}.
              </p>
              <label className="mt-1 flex items-center gap-2">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                Yes, change those approved weeks. The change is recorded against my name.
              </label>
            </>
          )}
          <div className="mt-2 flex flex-wrap justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPending(null)} disabled={busy}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => void commit()} disabled={busy || (pending.weeks.length > 0 && !ack)}>
              Confirm
            </Button>
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-1 text-xs text-ele-danger">
          {error}
        </p>
      )}
    </div>
  );
}
